// FudiTap guest behavioural events (Step 3): menu.view, product.view, cart.add
// -> POST /api/guest/events (1-20 per batch). Analytics only: events are batched,
// best-effort, never block the UI and never carry personal data. Scope is derived
// on the server from the entry key; products of another branch are dropped there.
import { entryBody, entryFrom } from "@/lib/guestEntry";
import { newIdempotencyKey } from "@/lib/guestOrder";

const TYPES = ["menu.view", "product.view", "cart.add"];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const EVENT_BATCH = 20;
export const EVENT_FLUSH_MS = 2000;

export function createEventTracker({ entry: rawEntry, baseUrl = "", fetchImpl = globalThis.fetch, newKey, schedule = setTimeout } = {}) {
  const entry = entryFrom(rawEntry);
  let queue = [];
  let timer = null;
  let retried = new Set();

  const send = async (batch) => {
    try {
      const res = await fetchImpl(`${baseUrl}/api/guest/events`, {
        method: "POST", credentials: "omit", cache: "no-store", redirect: "error", referrerPolicy: "no-referrer", keepalive: true,
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ...entryBody(entry), events: batch }),
      });
      if (res.status >= 500 || res.status === 429) throw new Error("retry");
    } catch {
      // one retry per event (same ids: the server deduplicates), then dropped
      const again = batch.filter((e) => !retried.has(e.id));
      again.forEach((e) => retried.add(e.id));
      if (again.length) { queue = [...again, ...queue].slice(0, 200); arm(); }
    }
  };
  const flush = async () => {
    timer = null;
    while (queue.length) {
      const batch = queue.slice(0, EVENT_BATCH);
      queue = queue.slice(EVENT_BATCH);
      await send(batch);
    }
  };
  function arm() { if (!timer) timer = schedule(() => { void flush(); }, EVENT_FLUSH_MS); }

  return {
    /** Returns false (and sends nothing) for anything outside the contract. */
    track(type, { productId, quantity } = {}) {
      if (!entry || typeof fetchImpl !== "function" || !TYPES.includes(type)) return false;
      let eventId;
      try { eventId = (newKey || (() => newIdempotencyKey()))(); } catch { return false; }   // no CSPRNG: no event
      const event = { id: eventId, type };
      if (type !== "menu.view") {
        if (typeof productId !== "string" || !UUID.test(productId)) return false;   // mock/demo ids never leave the device
        event.productId = productId.toLowerCase();
      }
      if (type === "cart.add") {
        if (!Number.isInteger(quantity) || quantity < 1 || quantity > 20) return false;
        event.quantity = quantity;
      }
      queue.push(event);
      if (queue.length >= EVENT_BATCH) { if (timer) { clearTimeout(timer); timer = null; } void flush(); } else arm();
      return true;
    },
    flush,
    pending: () => queue.length,
  };
}

// App-wide tracker for the loaded entry (set by GuestMenuGate in API mode only).
let active = null;
export function setActiveEventTracker(tracker) { active = tracker; }
export function trackGuestEvent(type, data) { try { return active ? active.track(type, data) : false; } catch { return false; } }
